// Added by Sejune Oh on 2019-05-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef, RefObject } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import { RouteChildrenProps } from 'react-router'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  SIGN_ERR_STATUS_CODE,
  InputBlurValidator,
  InputValidator,
  MgmtValidator,
  ReactEmptyEvent,
  routerPath,
} from '@/helper/define'

// Util
import { getFormData } from '@/helper/util'

import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Components
import ResetUserPassword from './ResetUserPassword'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import { UserStore, UserElementProps } from '@/stores/mgmt/user'
import { UserAuthStore } from '@/stores/user/UserAuth'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import withUserValidator from '../UserValidator'
import EventStore from '@/stores/EventStore'

interface ResetUserPasswordCProps extends MgmtValidator, RouteChildrenProps {
  userAuth?: UserAuthStore
  user?: UserStore
  globalEvt?: EventStore
  emailRef: RefObject<any>
  usernameRef: RefObject<any>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'user', 'globalEvt')
@observer
class ResetUserPasswordContainer extends Component<ResetUserPasswordCProps> {
  isErrorOccured: boolean = false
  errMessage: string = ''

  _formId = 'r-pw'
  _curPwdRef = createRef<any>()
  _newPwdRef = createRef<any>()
  _newConfirmPwdRef = createRef<any>()

  constructor(props: ResetUserPasswordCProps) {
    super(props)
    makeObservable(this, {
      isErrorOccured: observable,
      errMessage: observable,
      _handleSnackbarClose: action,
      _handlePasswordUpdate: action,
      userAuth: computed,
    })
  }

  render() {
    const { blurValidator: _blurValdiator, validator: _validator, ...others } = this.props
    const validator = {
      ..._validator,
      pwdValidator: this._pwdValidator,
      confirmPwdValidator: this._confirmPwdValidator,
    }
    const blurValidator = {
      ..._blurValdiator,
      pwdBlurValidator: this._pwdBlurValidator,
      confirmPwdBlurValidator: this._confirmPwdBlurValidator,
    }

    return (
      <ResetUserPassword
        {...others}
        formId={this._formId}
        validator={validator}
        blurValidator={blurValidator}
        userName={this.userAuth.userInfo!.username!}
        userEmail={this.userAuth.userInfo!.email!}
        curPwdRef={this._curPwdRef}
        newPwdRef={this._newPwdRef}
        newConfirmPwdRef={this._newConfirmPwdRef}
        onConfirmBtnClick={this._handlePasswordUpdate}
        onCancelBtnClick={this._handleCancelBtnClick}
        isErrorOccured={this.isErrorOccured}
        onSnackbarClose={this._handleSnackbarClose}
        errMessage={this.errMessage}
      />
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleSnackbarClose: ReactEmptyEvent = () => {
    this.isErrorOccured = false
  }

  _handlePasswordUpdate: ReactEmptyEvent = async () => {
    try {
      this.props.globalEvt!.setLoadingOn()

      const form = document.getElementById(this._formId) as HTMLFormElement
      const _curPwdRef = this._curPwdRef!.current!
      const changePwdInfo = this._getFormData(form)

      _curPwdRef.callValidator()

      if (!_curPwdRef.isErrorOccured) {
        await this.props.user!.updatePassword(changePwdInfo!)
        this.userAuth.clearPasswordInitialization()
      }
    } catch (err) {
      let errMessage = err.message

      if (err.response) {
        const { status } = err.response
        errMessage = this.userAuth.getSignInErrMessage(Intl, SIGN_ERR_STATUS_CODE[`${status}`])
      }

      this.errMessage = errMessage
      this.isErrorOccured = true
    } finally {
      this.props.globalEvt!.setLoadingOff()
    }
  }

  get userAuth() {
    return this.props.userAuth!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _getFormData = (form: HTMLFormElement) => {
    let formData = getFormData<UserElementProps>(form)
    formData!.id = this.userAuth.userInfo!.id
    return formData
  }

  _handleCancelBtnClick: ReactEmptyEvent = () => {
    this.props.history.replace(`/${routerPath.WORKLIST}`)
  }

  _pwdValidator: InputValidator = (value) => {
    if (this._newConfirmPwdRef.current) this._newConfirmPwdRef.current!.callValidator()
    return this.props.validator.pwdValidator(value)
  }

  _confirmPwdValidator: InputValidator = (value) => {
    const curPwd = this._newPwdRef.current!.value

    if (curPwd !== value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }

  _pwdBlurValidator: InputBlurValidator = async (value) => {
    if (this._newConfirmPwdRef.current) this._newConfirmPwdRef.current!.callValidator()
    return this.props.blurValidator!.pwdBlurValidator(value)
  }

  _confirmPwdBlurValidator: InputBlurValidator = async (value) => {
    const curPwd = this._newPwdRef!.current!.value

    if (curPwd !== value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpasswordcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }
}

export default withUserValidator()(ResetUserPasswordContainer)
